import type { components as BackendComponents } from "@/lib/api/generated/backend";

/**
 * A consultant's role in their own company (backend#1069).
 *
 * A `manager` chooses and signs the company's agreements and sees its
 * commercial terms and invoices; an `employee` time-reports and never sees
 * money. A one-person company is a company with one manager. Older backends
 * send no role at all, which reads as manager: today's behaviour.
 */
export type CompanyRole = NonNullable<
  BackendComponents["schemas"]["ConsultantOwnCompany"]["company_role"]
>;

export const COMPANY_ROLES: readonly CompanyRole[] = ["manager", "employee"];

export const COMPANY_ROLE_LABELS: Record<CompanyRole, string> = {
  manager: "Manager",
  employee: "Employee",
};

/** What each role means, for the admin's select. */
export const COMPANY_ROLE_HELP: Record<CompanyRole, string> = {
  manager: "Chooses and signs the company's agreements; sees its terms and invoices.",
  employee: "Time-reports only; sees no rates, terms, price options or invoices.",
};

/** The role to show for a profile: missing (older backend) means manager. */
export const companyRoleOf = (
  holder: { company_role?: CompanyRole | null } | null | undefined
): CompanyRole => holder?.company_role ?? "manager";

/**
 * Whether the signed-in user is an employee of a consultancy company, i.e.
 * must not see Financials. False while the user is loading or on an older
 * backend without the field.
 */
export const isCompanyEmployee = (
  user: { company_role?: CompanyRole | null } | null | undefined
): boolean => user?.company_role === "employee";

/** The admin list / detail fields that say who manages a consultancy (backend#1075). */
export interface CompanyManagement {
  company_role?: CompanyRole | null;
  /** Active consultants in the profile's company, the consultant included. */
  company_member_count?: number | null;
  company_name?: string | null;
}

/**
 * A manager of a company with more than one member: the one the admin
 * list marks with the "Manager" badge. A one-person company is its own
 * manager and gets no badge; older backends send neither field.
 */
export const isCompanyManager = (c: CompanyManagement | null | undefined): boolean =>
  c?.company_role === "manager" && (c.company_member_count ?? 0) > 1;

/** Tooltip of the badge: "Manages Example Supplier AB". */
export const managesLabel = (companyName: string | null | undefined): string =>
  companyName ? `Manages ${companyName}` : "Manages the company";
